import './Carousel.less';

import { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import cx from 'classnames';
import { defineMessages, useIntl } from 'react-intl';
import Slider from 'react-slick';
import { Message } from 'semantic-ui-react';

import { NextArrow, PrevArrow } from '../../SlickComponents/Arrows';

import Body from './CarouselBody';

import teaserHeroTopTemplate from './icons/teaser-template.svg';

const messages = defineMessages({
  PleaseChooseContent: {
    id: 'Please choose an existing content as source for this element',
    defaultMessage:
      'Please choose an existing content as source for this element',
  },
});

const DEFAULT_ASPECT_RATIO = {
  desktop: '56.25%',
  tablet: '75%',
  phone: '66.6666%',
};
const SLIDER_SPEED = 500;

const CarouselView = ({
  block,
  data,
  isEditMode,
  openObjectBrowser,
  onChangeBlock,
  bg_color,
  focusedIndex,
}) => {
  const {
    aspectRatioDesktop = DEFAULT_ASPECT_RATIO.desktop,
    aspectRatioTablet = DEFAULT_ASPECT_RATIO.tablet,
    aspectRatioPhone = DEFAULT_ASPECT_RATIO.phone,
    aspectRatioMode = 'fixed',
    autoplay,
    autoplaySpeed,
    columns = [],
    infinite,
    useLargeContainer,
    layout,
  } = data;

  const intl = useIntl();
  const sliderRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const TABLET_BREAKPOINT = 768;
  const DESKTOP_BREAKPOINT = 992;
  const [device, setDevice] = useState('phone');
  const handleResize = (e) => {
    if (e.target.innerWidth >= DESKTOP_BREAKPOINT) {
      setDevice('desktop');
    } else if (e.target.innerWidth >= TABLET_BREAKPOINT) {
      setDevice('tablet');
    } else {
      setDevice('phone');
    }
  };
  useEffect(() => {
    handleResize({ target: { innerWidth: window.innerWidth } });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: infinite,
    speed: SLIDER_SPEED,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: aspectRatioMode === 'free',
    beforeChange: (_old, next) => setCurrentSlide(next),
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    autoplay: autoplay,
    autoplaySpeed: autoplaySpeed ? autoplaySpeed * 1000 : null,
    pauseOnHover: true,
    pauseOnFocus: true,
    pauseOnDotsHover: true,
  };

  const filteredColumns = useMemo(() => {
    const avaliablesStates = ['published', null, undefined];
    return columns.filter((item) => {
      if (isEditMode) {
        return true;
      }

      if (item.withoutSource && !item.customized?.preview_image_desktop) {
        return false;
      }

      return avaliablesStates.includes(item.href?.[0]?.review_state);
    });
  }, [columns, isEditMode]);

  useEffect(() => {
    if (
      typeof focusedIndex === 'number' &&
      focusedIndex >= 0 &&
      focusedIndex < filteredColumns.length &&
      sliderRef.current
    ) {
      sliderRef.current.slickGoTo(focusedIndex);
    }
  }, [focusedIndex, filteredColumns.length]);

  useEffect(() => {
    if (!sliderRef.current) return;

    sliderRef.current.slickGoTo(currentSlide);
    sliderRef.current.innerSlider?.onWindowResized?.();
  }, [aspectRatioMode, currentSlide]);

  const renderEmptyMessage = () => (
    <Message>
      <div className="teaser-item default">
        <Icon name={teaserHeroTopTemplate} />
        <p>{intl.formatMessage(messages.PleaseChooseContent)}</p>
      </div>
    </Message>
  );

  const stopAutoPlay = () => {
    if (!autoplay) return;

    setIsPaused(true);
    if (sliderRef.current) {
      sliderRef.current.slickPause();
    }
  };

  const startAutoPlay = () => {
    if (!autoplay) return;

    setIsPaused(false);
    if (sliderRef.current) {
      sliderRef.current.slickPlay();
    }
  };

  const sliderClasses = cx('carousel-block__slider', {
    'full-width': useLargeContainer,
    'carousel-block__slider--paused': isPaused,
    edit: isEditMode,
  });

  const containerClasses = cx(
    'block carousel carousel-block',
    {
      'wrapperstyle full': useLargeContainer,
      'carousel-block__container--free': aspectRatioMode === 'free',
    },
    'carousel-block__container',
  );

  const sliderKey = `${aspectRatioMode}-${aspectRatioDesktop}-${aspectRatioTablet}-${aspectRatioPhone}`;

  const renderCarousel = () => (
    <div
      className={sliderClasses}
      style={{ backgroundColor: bg_color }}
      onMouseEnter={stopAutoPlay}
      onMouseLeave={startAutoPlay}
    >
      <span className={cx('carousel-block__pause-overlay', 'fas fa-pause')} />
      <Slider key={sliderKey} ref={sliderRef} {...sliderSettings}>
        {filteredColumns.map((item, index) => (
          <Body
            key={`${item['@id']}-${index}`}
            data={item}
            isEditMode={isEditMode}
            dataBlock={data}
            index={index}
            block={block}
            openObjectBrowser={openObjectBrowser}
            onChangeBlock={onChangeBlock}
            layout={layout}
            overwrite={item.overwrite}
            device={device}
            onSlideMediaLoad={() => {
              if (aspectRatioMode !== 'free') return;
              if (typeof window === 'undefined') return;
              window.requestAnimationFrame(() => {
                sliderRef.current?.innerSlider?.onWindowResized?.();
              });
            }}
          />
        ))}
      </Slider>
    </div>
  );

  return (
    <div
      className={containerClasses}
      style={{
        '--carousel-user-ar-phone': aspectRatioPhone,
        '--carousel-user-ar-tablet': aspectRatioTablet,
        '--carousel-user-ar-desktop': aspectRatioDesktop,
      }}
    >
      {filteredColumns.length === 0
        ? isEditMode
          ? renderEmptyMessage()
          : null
        : renderCarousel()}
    </div>
  );
};

export default CarouselView;
